import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Copy, Check, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { cncEventCopy } from '../services/api';

/**
 * Marketing > C&C Event Copy.
 *
 * Built 2026-09-01 after the 2026-08-29 event went out titled "It's 5 O'Clock
 * Somewhere Motown Crab & Cruise®" - duplicated from last year's Motown event,
 * body rewritten, title missed, guests confused at the feast.
 *
 * Meta does not let any app create or edit a Facebook event, so this page does
 * not post. It hands over finished, correct text to paste into Business Suite,
 * built entirely from Event Management. The point is that duplicating an old
 * event stops being the easy path, because there is an easier one.
 */

const STATUS = {
  missing: { label: 'No Facebook event', color: 'var(--orange)', bg: '#FDF0EA' },
  title_differs: { label: 'Title does not match', color: '#B45309', bg: '#FEF3C7' },
  match: { label: 'On Facebook', color: '#15803D', bg: '#DCFCE7' },
  unknown: { label: 'Could not check Facebook', color: '#6B7280', bg: '#F3F4F6' },
};

function CopyButton({ text, label }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch (e) {
      // Clipboard is blocked in some contexts; the textarea below is always
      // selectable by hand, so this must never look like the copy is broken.
      window.prompt('Copy with Ctrl+C, then press Escape', text);
    }
  };
  return (
    <button className="btn btn-primary" onClick={copy} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {done ? <Check size={14} /> : <Copy size={14} />}
      {done ? 'Copied' : label}
    </button>
  );
}

export default function CncEventCopy() {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setList(await cncEventCopy('list')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEvent = async (id) => {
    setSelected(id); setDetail(null); setDetailLoading(true);
    try { setDetail(await cncEventCopy('generate', { id })); }
    catch (e) { setError(e.message); }
    finally { setDetailLoading(false); }
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="breadcrumb">
        <a href="#marketing">Marketing</a><span> / </span><span>C&amp;C Event Copy</span>
      </div>

      <h1 style={{ color: 'var(--navy)', marginBottom: 4 }}>Crab &amp; Cruise Event Copy</h1>
      <p style={{ color: '#4B5563', maxWidth: 780, marginTop: 0 }}>
        Built from Event Management, which is the record of truth for genre, times and prices.
        Facebook does not let us post events through the API, so copy the title and description
        below and paste them into Meta Business Suite. Because every fact comes from the event
        record, the copy cannot carry another event&apos;s genre, date or price.
      </p>

      {error && (
        <div className="card" style={{ borderLeft: '4px solid var(--orange)', marginBottom: 16 }}>
          <div className="card-body" style={{ color: 'var(--orange)' }}>{error}</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 20, alignItems: 'start' }}>
        {/* ── Upcoming events ── */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><Calendar size={16} style={{ verticalAlign: -3, marginRight: 6 }} />Upcoming events</span>
            <button className="btn btn-ghost" onClick={load} title="Refresh">
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loading && <div style={{ padding: 16, color: '#6B7280' }}>Loading…</div>}

            {!loading && list && list.fbError && (
              <div style={{ padding: '10px 16px', background: '#F3F4F6', color: '#6B7280', fontSize: 13 }}>
                Could not read Facebook right now, so the status column is unknown. The event
                details and the generated copy below are unaffected.
              </div>
            )}

            {!loading && list && list.events.length === 0 && (
              <div style={{ padding: 16, color: '#6B7280' }}>No upcoming events in Event Management.</div>
            )}

            {!loading && list && list.events.map((e) => {
              const st = STATUS[e.facebook.status] || STATUS.unknown;
              const active = selected === e.id;
              return (
                <button
                  key={e.id}
                  onClick={() => openEvent(e.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                    padding: '12px 16px', border: 'none', borderLeft: active ? '4px solid var(--orange)' : '4px solid transparent',
                    borderBottom: '1px solid #E5E7EB', background: active ? '#FDF7F4' : '#fff',
                  }}
                >
                  <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{e.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 6px' }}>{e.ticketDate}</div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                    color: st.color, background: st.bg, padding: '2px 8px', borderRadius: 10,
                  }}>{st.label}</span>
                  {e.facebook.status === 'title_differs' && (
                    <div style={{ fontSize: 12, color: '#B45309', marginTop: 6 }}>
                      Facebook says: “{e.facebook.name}”
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Generated copy ── */}
        <div className="card">
          <div className="card-header">Facebook event copy</div>
          <div className="card-body">
            {!selected && <div style={{ color: '#6B7280' }}>Pick an event on the left.</div>}
            {detailLoading && <div style={{ color: '#6B7280' }}>Building the copy…</div>}

            {detail && (
              <>
                {(detail.copy.warnings.length > 0 || detail.copy.brandIssues.length > 0) && (
                  <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, color: '#92400E', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <AlertTriangle size={15} /> Check before you post
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, color: '#78350F', fontSize: 13 }}>
                      {detail.copy.warnings.map((w, i) => <li key={`w${i}`} style={{ marginBottom: 4 }}>{w}</li>)}
                      {detail.copy.brandIssues.map((w, i) => <li key={`b${i}`} style={{ marginBottom: 4 }}>{w}</li>)}
                    </ul>
                  </div>
                )}

                <FactStrip ev={detail.event} />

                <Field label="Event title" value={detail.copy.title} rows={1} copyLabel="Copy title" />
                <Field label="Description" value={detail.copy.description} rows={18} copyLabel="Copy description" />
                <Field label="Ticket link" value={detail.copy.ticketUrl} rows={1} copyLabel="Copy link" />

                <div style={{ marginTop: 12, fontSize: 13, color: '#4B5563' }}>
                  Paste the ticket link into Facebook&apos;s own <strong>Ticket URL</strong> field as well as the
                  description. That field was empty on the August 29 event, so there was no Get Tickets button.
                </div>

                <a
                  className="btn btn-outline"
                  href="https://business.facebook.com/latest/events"
                  target="_blank" rel="noreferrer"
                  style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  Open Meta Business Suite <ExternalLink size={14} />
                </a>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The canonical facts, shown so the person pasting can see what it was built from. */
function FactStrip({ ev }) {
  const rows = [
    ['Genre', ev.genre],
    ['Date', ev.dateLong],
    ['Crabs', ev.crabSize],
    ['Gates', ev.gateStart],
    ['Feast', ev.feastStart && ev.feastEnd ? `${ev.feastStart} to ${ev.feastEnd}` : null],
    ev.kind === 'cruise'
      ? ['Cruise', ev.cruiseStart && ev.cruiseEnd ? `${ev.cruiseStart} to ${ev.cruiseEnd} (board ${ev.boarding})` : null]
      : ['Concert', ev.concertStart && ev.concertEnd ? `${ev.concertStart} to ${ev.concertEnd}` : null],
    ['DJs', [ev.upperDj, ev.lowerDj].filter(Boolean).join(' / ') || 'none recorded'],
  ].filter(([, v]) => v);

  return (
    <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#6B7280', marginBottom: 8 }}>
        From Event Management
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px 16px' }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ fontSize: 13 }}>
            <span style={{ color: '#6B7280' }}>{k}: </span>
            <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 13 }}>
        <span style={{ color: '#6B7280' }}>Tickets: </span>
        {ev.tiers.map((t) => (
          <span key={t.name} style={{ marginRight: 12, color: t.soldOut ? '#B45309' : 'var(--navy)' }}>
            {t.name} ${t.price.toFixed(2)}{t.soldOut ? ' (sold out)' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

function Field({ label, value, rows, copyLabel }) {
  return (
    <div className="form-group" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label className="form-label" style={{ margin: 0 }}>{label}</label>
        <CopyButton text={value} label={copyLabel} />
      </div>
      <textarea
        className="form-textarea"
        readOnly
        value={value}
        rows={rows}
        onFocus={(e) => e.target.select()}
        style={{ width: '100%', fontFamily: rows > 1 ? 'ui-monospace, Menlo, Consolas, monospace' : undefined, fontSize: 13 }}
      />
    </div>
  );
}
